import { createModel } from '@rematch/core';
import _ from 'lodash';

import { RootModel } from '../index';
import { apiUrls } from '../../api/apiUrls';
import { get } from '../../api/api';
import FilterState from '../../interfaces/FiltersState';
import { ListSport } from '../../interfaces/EventsState';

export default createModel<RootModel>()({
  state: {
    filterResults: [],
    topResults: [],
    optionsType: {
      horses: true,
      dogs: true,
      carriages: true
    },
    calendarType: 'calendar',
    selectedCountries: [],
    scrollPosition: 0,
    product: 'sports',
    firstProduct: true
  } as FilterState,

  reducers:{
    setFilterResults(state, payload: ListSport[]) {
      return {
        ...state,
        filterResults: payload
      };
    },

    setEventsTop(state, payload: ListSport[]) {
      return {
        ...state,
        topResults: payload
      };
    },

    setAllOptions(state, payload: { horses: boolean, dogs: boolean, carriages: boolean }) {
      return {
        ...state,
        optionsType: payload
      };
    },

    setOptions(state, payload: string) {
      return {
        ...state,
        optionsType: { ...state.optionsType, [payload]: !state.optionsType[payload] }
      };
    },

    setCalendarType(state, payload: string) {
      return { ...state, calendarType: payload };
    },

    setSelectedCountries(state, payload: number[]){
      return { ...state, selectedCountries: payload };
    },

    setScrollPosition(state, payload: number) {
      return { ...state, scrollPosition: payload };
    },

    setProduct(state, payload:string){
      return { ...state, product: payload, firstProduct: true };
    },

    deactivateProduct(state){
      return { ...state, firstProduct: false };
    }
  },

  effects: (dispatch) => ({
    clearFilterResults(): void {
      dispatch.filters.setFilterResults([]);
    },

    async setAllOptionsTypes(payload: { horses: boolean, dogs: boolean, carriages: boolean }): Promise<void> {
      dispatch.filters.setAllOptions(payload);
    },

    async setOptionsType(payload: string, state): Promise<void> {
      dispatch.filters.setOptions(payload);
      state.filters.optionsType;
    },

    async getFilterResults(payload: string[], state): Promise<void> {
      try {
        const { type, rangeHours, tournaments } = state.events.filterSports;

        let mode = 'prematch';
        if (type === '2,8') {
          mode = 'live';
        } else if (type === '4') {
          mode = 'outright';
        }

        dispatch.events.setLoading(true);
        const response = await get(apiUrls.sportEventsList +
          `?mode=${ mode }&range=${ rangeHours }&tournaments=${ _.join(payload ? payload : tournaments, ',') }`
        );

        if (response && response.status === 200) {
          const stateEvents = _.reduce(JSON.parse(JSON.stringify(state.filters.filterResults)), (prev, curr) => {
            curr.tournaments = _.filter(curr.tournaments, tournament => tournaments.indexOf(tournament.code.toString()) !== -1);
            if (curr.tournaments.length > 0) {
              prev.push(curr);
            }

            return prev;
          }, []);

          for (const res of response.data.results) {
            const foundSport = _.find(stateEvents, e => e.code === res.code);

            if (foundSport) {
              for (const tournament of res.tournaments) {
                const foundTournament = _.find(foundSport.tournaments, t => t.code === tournament.code);

                if (!foundTournament) {
                  foundSport.tournaments.unshift(tournament);
                } else {
                  const diff = _.difference(tournament.events, foundTournament.events);
                  for (const tournament of foundSport.tournaments) {
                    tournament.events = { ...tournament.events, ...diff };
                  }
                }
              }
            } else {
              stateEvents.unshift(res);
            }
          }

          await dispatch.filters.setFilterResults(stateEvents);

          dispatch.events.setLoading(false);
        } else {
          dispatch.events.setLoading(false);
        }
      } catch (error) {
        //TODO: HANDLE ERROR
        dispatch.events.setLoading(false);
      }
    },

    async getEventsByTop(payload: string, state): Promise<void> {
      try {
        dispatch.events.setSelectedTop(payload);

        dispatch.events.setLoading(true);
        await dispatch.events.resetSearchResults();
        const response = await get(
          `${ apiUrls.sportEventsList }?type=${ _.sumBy(state.events.filterSports.type.split(','), (t) => Number(t)) }&top=${ payload }`
        );

        if (response && response.status === 200) {
          await dispatch.filters.setEventsTop(response.data.results);
          dispatch.events.setLoading(false);
        } else {
          dispatch.events.setLoading(false);
        }
      } catch (error) {
        //TODO: HANDLE ERROR
        dispatch.events.setLoading(false);
      }
    },

    updateCalendarType(payload) {
      dispatch.filters.setCalendarType(payload);
    },

    updateScrollPosition(payload: number) {
      dispatch.filters.setScrollPosition(payload);
    },

    updateSelectedCountries(payload: number[]): void{
      dispatch.filters.setSelectedCountries(payload);
    },

    updateProduct(payload:string){
      dispatch.filters.setProduct(payload);
    },

    deactivateFirstProduct(){
      dispatch.filters.deactivateProduct();
    }
  }),

  selectors: (slice, createSelector) => ({
    total() {
      return createSelector(
        slice,
        (rootState) => rootState.events,
        (state, { eventsTree, filterSports }) => {
          let totalResults = 0;

          for (const sport of eventsTree) {
            for (const category of sport.categories) {
              for (const tournament of category.tournaments) {
                if (filterSports.tournaments.indexOf(tournament.code.toString()) !== -1) {
                  totalResults += (tournament['eventsCount'] || 0);
                }
              }
            }
          }

          return totalResults;
        }
      );
    },

    totalTopResults() {
      return slice(state => {
        let totalResults = 0;

        for (const sport of state.filterResults) {
          for (const tournament of sport.tournaments) {
            totalResults += (tournament['eventsCount'] || 0);
          }
        }

        return totalResults;
      });
    }
  })
});
